import {
  coordEq,
  coordKey,
  getTile,
  hasActiveCapture,
  previewCapture,
  type Coord,
  type GameState,
  type PlayerId,
  type Unit,
} from "@dailywars/core/engine";

import { canCaptureAt } from "#/game/selection/transitions";
import type { SelectionState } from "#/game/selection/types";

export type CaptureBarHoverPreview = {
  tileKey: string;
  filledCount: number;
  previewFilledCount: number;
  owner: PlayerId;
};

function capturePosForSelection({
  selection,
  unit,
}: {
  selection: Extract<SelectionState, { mode: "unit" | "move-preview" }>;
  unit: Unit;
}): Coord {
  return selection.mode === "move-preview" ? selection.destination : unit.pos;
}

export function captureBarHoverPreview({
  state,
  selection,
  hoveredTile,
}: {
  state: GameState;
  selection: SelectionState;
  hoveredTile: Coord | null;
}): CaptureBarHoverPreview | null {
  if (!hoveredTile) return null;
  if (selection.mode !== "unit" && selection.mode !== "move-preview") return null;

  const unit = state.units.find((candidate) => candidate.id === selection.unitId);
  if (!unit) return null;

  const capturePos = capturePosForSelection({ selection, unit });
  if (!coordEq(hoveredTile, capturePos)) return null;
  if (!canCaptureAt({ state, unit, pos: capturePos })) return null;

  const tile = getTile(state, capturePos);
  if (!hasActiveCapture(tile)) return null;

  const { from, to } = previewCapture({ tile, unit });
  if (from >= to) return null;

  return {
    tileKey: coordKey(capturePos),
    filledCount: from,
    previewFilledCount: to,
    owner: unit.owner,
  };
}
